import React from "react";
import {
  Shield,
  Lock,
  MapPin,
  Camera,
  CreditCard,
  Bell,
  Database,
  Users,
  FileText,
  Mail,
  Phone,
} from "lucide-react";
import { motion } from "framer-motion";

const PrivacyPolicy = () => {
  const sections = [
    {
      icon: <Users className="w-5 h-5" />,
      title: "Information We Collect",
      points: [
        "Full Name",
        "Mobile Number",
        "Email Address",
        "Address",
        "Vehicle Information",
        "Service Booking Details",
        "Profile Picture (Optional)",
      ],
    },
    {
      icon: <MapPin className="w-5 h-5" />,
      title: "Location Information",
      description:
        "With your permission, we may collect your device location to provide doorstep services, locate nearby mechanics, improve booking accuracy, and track technician arrival.",
    },
    {
      icon: <Camera className="w-5 h-5" />,
      title: "Camera & Gallery Access",
      description:
        "Camera and gallery permissions are used only for uploading vehicle photos, service verification, damage reporting, and updating your profile picture.",
    },
    {
      icon: <CreditCard className="w-5 h-5" />,
      title: "Payment Information",
      description:
        "Payments are securely processed through trusted payment gateways. We never store your debit card, credit card, CVV, UPI PIN, or banking passwords.",
    },
    {
      icon: <Database className="w-5 h-5" />,
      title: "How We Use Your Information",
      points: [
        "Create and manage your account",
        "Process service bookings",
        "Assign mechanics",
        "Provide customer support",
        "Process payments and refunds",
        "Improve user experience",
        "Prevent fraud and misuse",
        "Comply with legal obligations",
      ],
    },
    {
      icon: <Bell className="w-5 h-5" />,
      title: "Notifications",
      description:
        "We may send booking confirmations, mechanic updates, payment confirmations, service reminders, and promotional offers. Promotional notifications can be disabled anytime.",
    },
    {
      icon: <Lock className="w-5 h-5" />,
      title: "Data Security",
      description:
        "Your information is protected using HTTPS encryption, secure servers, authentication mechanisms, and restricted database access.",
    },
    {
      icon: <Shield className="w-5 h-5" />,
      title: "Sharing of Information",
      description:
        "We never sell your personal information. Information is shared only with assigned mechanics, authorized service centers, payment partners, and government authorities when legally required.",
    },
    {
      icon: <FileText className="w-5 h-5" />,
      title: "Your Rights",
      points: [
        "Access your personal information",
        "Correct inaccurate information",
        "Delete your account",
        "Request a copy of your data",
        "Withdraw consent where applicable",
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero */}
      <div className="relative bg-[#111111] border-b border-white/10 py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "linear-gradient(#FF6B35 1px, transparent 1px), linear-gradient(90deg,#FF6B35 1px, transparent 1px)",
            backgroundSize: "50px 50px",
          }}
        />

        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-[#FF6B35] rounded-full blur-[120px] opacity-10" />

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative max-w-4xl mx-auto text-center"
        >
          <div className="w-16 h-16 rounded-2xl bg-[#FF6B35]/10 border border-[#FF6B35]/20 flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-[#FF6B35]" />
          </div>

          <span className="px-4 py-2 rounded-full bg-[#FF6B35]/10 border border-[#FF6B35]/20 text-[#FF6B35] text-xs uppercase tracking-widest font-bold">
            Privacy Policy
          </span>

          <h1 className="text-4xl md:text-5xl font-black text-white mt-6 mb-4">
            Your Privacy Matters
          </h1>

          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            We are committed to protecting your personal information and being
            transparent about how we collect, use, and safeguard your data.
          </p>
        </motion.div>
      </div>

      {/* Intro */}
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="bg-[#111111] border border-white/10 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-white mb-4">
            Effective Date
          </h2>

          <p className="text-gray-400 leading-8">
            This Privacy Policy explains how <strong>OkMechanic</strong>
            collects, uses, stores, and protects your personal information when
            you use our website, mobile application, and vehicle servicing
            platform.
          </p>

          <p className="text-gray-400 mt-4 leading-8">
            By using OkMechanic, you agree to the practices described in this
            Privacy Policy.
          </p>
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-5xl mx-auto px-4 pb-10">
        <div className="grid md:grid-cols-2 gap-5">
          {sections.map((section, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-[#111111] border border-white/10 rounded-2xl p-6 hover:border-[#FF6B35]/30 transition"
            >
              <div className="w-11 h-11 rounded-xl bg-[#FF6B35]/10 border border-[#FF6B35]/20 flex items-center justify-center text-[#FF6B35] mb-4">
                {section.icon}
              </div>

              <h3 className="text-lg font-bold text-white mb-3">
                {section.title}
              </h3>

              {section.description && (
                <p className="text-gray-400 leading-7">
                  {section.description}
                </p>
              )}

              {section.points && (
                <ul className="space-y-2 text-gray-400 text-sm">
                  {section.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="text-[#FF6B35] mt-1">•</span>
                      {point}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      {/* Contact */}
      <div className="max-w-5xl mx-auto px-4 pb-16">
        <div className="bg-[#FF6B35]/10 border border-[#FF6B35]/20 rounded-2xl p-8">
          <h2 className="text-2xl font-bold text-white text-center mb-6">
            Contact Us
          </h2>

          <div className="grid md:grid-cols-2 gap-4">
            <a
              href="tel:+919923887090"
              className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10 hover:border-[#FF6B35]/30 transition"
            >
              <Phone className="text-[#FF6B35]" />
              <div>
                <p className="text-sm text-gray-400">Phone</p>
                <p className="text-white font-semibold">
                  +91 9923887090
                </p>
              </div>
            </a>

            <a
              href="mailto:okmechanics@gmail.com"
              className="flex items-center gap-4 bg-white/5 rounded-xl p-4 border border-white/10 hover:border-[#FF6B35]/30 transition"
            >
              <Mail className="text-[#FF6B35]" />
              <div>
                <p className="text-sm text-gray-400">Email</p>
                <p className="text-white font-semibold">
                  okmechanics@gmail.com
                </p>
              </div>
            </a>
          </div>

          <p className="text-center text-gray-400 text-sm mt-8">
            If you have any questions regarding this Privacy Policy or your
            personal data, please contact us using the above details.
          </p>
        </div>

        <p className="text-center text-xs text-gray-600 mt-6">
          Last Updated: July 2026 • © OkMechanic. All Rights Reserved.
        </p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;